<template>
  <div class="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <!-- 상단 필터 섹션 -->
    <div class="flex items-center justify-between mb-8">
      <div class="flex items-center gap-4">
        <h1 class="text-2xl font-semibold text-gray-900">
          사용자 활동 대시보드
        </h1>
        <input
          type="date"
          v-model="selectedDate"
          class="px-4 py-2 border rounded-md text-gray-600 text-base"
          :max="today"
        />
      </div>
      <div class="flex items-center gap-4">
        <select
          v-model="selectedUser"
          class="px-4 py-2 border rounded-md text-gray-600 text-base"
        >
          <option value="">전체 사용자</option>
          <option v-for="user in uniqueUsers" :key="user" :value="user">
            {{ user }}
          </option>
        </select>
      </div>
    </div>

    <!-- 사용자 활동 요약 -->
    <div class="grid grid-cols-5 gap-6 mb-8">
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-sm font-medium text-gray-500">총 조회 횟수</h3>
        <p class="mt-2 text-3xl font-semibold text-gray-900">
          {{ totalActivities }}
        </p>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-sm font-medium text-gray-500">호가 목록 조회</h3>
        <p class="mt-2 text-3xl font-semibold text-gray-600">
          {{ filteredAskListLogs.length }}
        </p>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-sm font-medium text-gray-500">체결된 호가 조회</h3>
        <p class="mt-2 text-3xl font-semibold text-gray-600">
          {{ filteredAskSoldOutLogs.length }}
        </p>
      </div>

      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-sm font-medium text-gray-500">매도중인 호가 조회</h3>
        <p class="mt-2 text-3xl font-semibold text-gray-600">
          {{ filteredAskOnSaleLogs.length }}
        </p>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-sm font-medium text-gray-500">거래내역 조회</h3>
        <p class="mt-2 text-3xl font-semibold text-gray-600">
          {{ filteredTransactionListLogs.length }}
        </p>
      </div>
    </div>

    <!-- 로그 테이블 섹션 -->
    <div class="flex flex-col gap-8">
      <div class="flex flex-wrap gap-8">
        <!-- 매도 호가 조회 로그 -->
        <div class="flex-1 min-w-[calc(50%-1rem)] bg-white rounded-lg shadow">
          <div
            class="px-6 py-4 border-b border-gray-200 flex justify-between items-center"
          >
            <div>
              <h2 class="text-lg font-medium text-gray-900">호가 목록 조회</h2>
            </div>
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  v-model="excludeDefaultMonths"
                  class="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <span>0개월/MAX 제외</span>
              </label>
              <label class="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  v-model="deduplicateAskList"
                  class="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <span>중복제거</span>
              </label>
              <div class="flex gap-2">
                <div
                  v-for="(count, type) in creditTypeStats"
                  :key="type"
                  :class="getCreditTypeClass(type, selectedAskTypes.has(type))"
                  @click="toggleAskType(type)"
                >
                  {{ formatCreditType(type) }}: {{ count }}
                </div>
              </div>
            </div>
          </div>
          <div class="overflow-x-auto" style="max-height: 500px">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    시간
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    사용자
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    조회일자
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    채권유형
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    최소만기
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    최대만기
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    검색어
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="log in filteredAskListLogs" :key="log.id">
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ formatTime(log.createdAt) }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ log.username }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ formatDate(log.metadata.date) }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    <span :class="getCreditTypeClass(log.metadata.creditType)">
                      {{ formatCreditType(log.metadata.creditType) }}
                    </span>
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ formatMonths(log.metadata.minMonths) }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ formatMonths(log.metadata.maxMonths) }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ log.metadata.searchKeyword || '-' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 거래 내역 조회 로그 -->
        <div class="flex-1 min-w-[calc(50%-1rem)] bg-white rounded-lg shadow">
          <div
            class="p-6 border-b border-gray-200 flex justify-between items-center"
          >
            <div>
              <h2 class="text-lg font-medium text-gray-900">거래 내역 조회</h2>
            </div>
            <div class="flex items-center gap-4">
              <label class="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  v-model="excludeDefaultMonthsTransaction"
                  class="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <span>0개월/MAX 제외</span>
              </label>
              <label class="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  v-model="deduplicateTransactionList"
                  class="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <span>중복제거</span>
              </label>
              <div class="flex gap-2">
                <div
                  v-for="(count, type) in transactionCreditTypeStats"
                  :key="type"
                  :class="
                    getCreditTypeClass(type, selectedTransactionTypes.has(type))
                  "
                  @click="toggleTransactionType(type)"
                >
                  {{ formatCreditType(type) }}: {{ count }}
                </div>
              </div>
            </div>
          </div>
          <div class="overflow-x-auto" style="max-height: 500px">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    시간
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    사용자
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    채권유형
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    최소만기
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    최대만기
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    검색어
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="log in filteredTransactionListLogs" :key="log.id">
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ formatTime(log.createdAt) }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ log.username }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    <span :class="getCreditTypeClass(log.metadata.creditType)">
                      {{ formatCreditType(log.metadata.creditType) }}
                    </span>
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ formatMonths(log.metadata.minMonths) }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ formatMonths(log.metadata.maxMonths) }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ log.metadata.searchKeyword || '-' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 체결 호가 조회 로그 -->
        <div class="flex-1 min-w-[calc(50%-1rem)] bg-white rounded-lg shadow">
          <div
            class="px-6 py-4 border-b border-gray-200 flex justify-between items-center"
          >
            <div>
              <h2 class="text-lg font-medium text-gray-900">
                체결 호가 상세 조회
              </h2>
            </div>
          </div>
          <div class="overflow-x-auto" style="max-height: 400px">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    시간
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    사용자
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    내용
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="log in filteredAskSoldOutLogs" :key="log.id">
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ formatTime(log.createdAt) }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ log.username }}
                  </td>
                  <td class="px-4 py-4 text-sm">
                    {{ log.metadata.content }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 매도 호가 코드 조회 -->
        <div class="flex-1 min-w-[calc(50%-1rem)] bg-white rounded-lg shadow">
          <div
            class="px-6 py-4 border-b border-gray-200 flex justify-between items-center"
          >
            <div>
              <h2 class="text-lg font-medium text-gray-900">
                매도 호가 코드 조회
              </h2>
            </div>
          </div>
          <div class="overflow-x-auto" style="max-height: 400px">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50 sticky top-0">
                <tr>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    시간
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    사용자
                  </th>
                  <th
                    class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    내용
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="log in filteredAskOnSaleLogs" :key="log.id">
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ formatTime(log.createdAt) }}
                  </td>
                  <td class="px-4 py-4 whitespace-nowrap text-sm">
                    {{ log.username }}
                  </td>
                  <td class="px-4 py-4 text-sm">
                    {{ log.metadata.content }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { getTodayStr } from '@/utils/util';
import userService from '@/service/userService';

export default {
  name: 'UserLogView',

  setup() {
    const askListLogs = ref([]);
    const askSoldOutLogs = ref([]);
    const askOnSaleLogs = ref([]);
    const transactionListLogs = ref([]);
    const selectedDate = ref(getTodayStr());
    const selectedUser = ref('');
    const today = ref(getTodayStr());

    const CREDIT_TYPE_ORDER = ['PUBLIC', 'BANK', 'CREDIT_FINANCE', 'COMPANY'];

    // 사용자 목록 계산
    const uniqueUsers = computed(() => {
      const users = new Set();
      [
        ...askListLogs.value,
        ...askSoldOutLogs.value,
        ...askOnSaleLogs.value,
        ...transactionListLogs.value,
      ].forEach(log => {
        users.add(log.username);
      });
      return Array.from(users).sort();
    });

    // 필터 상태 추가
    const selectedAskTypes = ref(new Set(CREDIT_TYPE_ORDER));
    const selectedTransactionTypes = ref(new Set(CREDIT_TYPE_ORDER));

    // 중복제거 토글 상태 추가
    const deduplicateAskList = ref(false);
    const deduplicateTransactionList = ref(false);

    // 중복 제거 로직을 위한 유틸리티 함수
    const getLogKey = log => {
      const metadata = log.metadata;
      return `${log.username}_${metadata.date}_${metadata.creditType}_${metadata.minMonths}_${metadata.maxMonths}_${metadata.searchKeyword}`;
    };

    // 필터링된 로그 수정 (중복제거 로직 추가)
    const excludeDefaultMonths = ref(false);
    const excludeDefaultMonthsTransaction = ref(false);

    const filteredAskListLogs = computed(() => {
      let filtered = askListLogs.value.filter(
        log =>
          (!selectedUser.value || log.username === selectedUser.value) &&
          (selectedAskTypes.value.size === 0 ||
            selectedAskTypes.value.has(log.metadata.creditType)) &&
          (!excludeDefaultMonths.value ||
            (log.metadata.minMonths !== '0' && log.metadata.maxMonths !== '61'))
      );

      if (deduplicateAskList.value) {
        const seen = new Set();
        filtered = filtered.filter(log => {
          const key = getLogKey(log);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }

      return filtered;
    });

    const filteredAskSoldOutLogs = computed(() => {
      return askSoldOutLogs.value.filter(
        log => !selectedUser.value || log.username === selectedUser.value
      );
    });

    const filteredAskOnSaleLogs = computed(() => {
      return askOnSaleLogs.value.filter(
        log => !selectedUser.value || log.username === selectedUser.value
      );
    });

    const filteredTransactionListLogs = computed(() => {
      let filtered = transactionListLogs.value.filter(
        log =>
          (!selectedUser.value || log.username === selectedUser.value) &&
          (selectedTransactionTypes.value.size === 0 ||
            selectedTransactionTypes.value.has(log.metadata.creditType)) &&
          (!excludeDefaultMonthsTransaction.value ||
            (log.metadata.minMonths !== '0' && log.metadata.maxMonths !== '61'))
      );

      if (deduplicateTransactionList.value) {
        const seen = new Set();
        filtered = filtered.filter(log => {
          const key = getLogKey(log);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }

      return filtered;
    });

    // 통계
    const totalActivities = computed(
      () =>
        filteredAskListLogs.value.length +
        filteredAskSoldOutLogs.value.length +
        filteredAskOnSaleLogs.value.length +
        filteredTransactionListLogs.value.length
    );

    const creditTypeStats = computed(() => {
      const stats = {};
      // 먼저 모든 유형을 0으로 초기화
      CREDIT_TYPE_ORDER.forEach(type => {
        stats[type] = 0;
      });
      // 실제 카운트 집계
      filteredAskListLogs.value.forEach(log => {
        const type = log.metadata.creditType;
        if (type) stats[type] = (stats[type] || 0) + 1;
      });
      // 정렬된 객체 반환
      return Object.fromEntries(
        CREDIT_TYPE_ORDER.map(type => [type, stats[type]])
      );
    });

    const statusStats = computed(() => {
      const stats = {};
      filteredAskSoldOutLogs.value.forEach(log => {
        const type = log.metadata.creditType;
        stats[type] = (stats[type] || 0) + 1;
      });
      return stats;
    });

    // 거래내역 조회 유형별 통계 추가
    const transactionCreditTypeStats = computed(() => {
      const stats = {};
      // 먼저 모든 유형을 0으로 초기화
      CREDIT_TYPE_ORDER.forEach(type => {
        stats[type] = 0;
      });
      // 실제 카운트 집계
      filteredTransactionListLogs.value.forEach(log => {
        const type = log.metadata.creditType;
        if (type) stats[type] = (stats[type] || 0) + 1;
      });
      // 정렬된 객체 반환
      return Object.fromEntries(
        CREDIT_TYPE_ORDER.map(type => [type, stats[type]])
      );
    });

    const formatTime = dateTime => {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleTimeString('ko-KR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    };

    const formatDate = date => {
      if (!date) return '-';
      const dateObj = new Date(date);
      const dayDiff = new Date().getDate() - dateObj.getDate();
      const desc = dayDiff > 1 ? `(${dayDiff}일 전)` : '';
      return `${date} ${desc}`;
    };

    const formatCreditType = type => {
      const types = {
        PUBLIC: '공사채',
        BANK: '은행채',
        CREDIT_FINANCE: '여전채',
        COMPANY: '회사채',
      };
      return types[type] || type;
    };

    const getCreditTypeClass = (type, isSelected = true) => {
      const baseClasses =
        'px-2 py-1 rounded-full text-xs font-medium cursor-pointer';
      const classes = {
        PUBLIC: isSelected
          ? 'bg-blue-100 text-blue-800'
          : 'bg-white text-blue-800 border border-blue-300',
        BANK: isSelected
          ? 'bg-green-100 text-green-800'
          : 'bg-white text-green-800 border border-green-300',
        CREDIT_FINANCE: isSelected
          ? 'bg-yellow-100 text-yellow-800'
          : 'bg-white text-yellow-800 border border-yellow-300',
        COMPANY: isSelected
          ? 'bg-purple-100 text-purple-800'
          : 'bg-white text-purple-800 border border-purple-300',
      };
      return `${baseClasses} ${classes[type] || ''}`;
    };

    const formatMonths = months => {
      if (months === '61') return 'MAX';
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      if (years === 0) return `${remainingMonths}개월`;
      if (remainingMonths === 0) return `${years}년`;
      return `${years}년 ${remainingMonths}개월`;
    };

    const fetchLogs = async () => {
      try {
        const result = await userService.getUserLogs(selectedDate.value);
        if (result) {
          askListLogs.value = result.filter(
            log => log.eventType === 'CREDIT_QUOTE_LIST'
          );
          askSoldOutLogs.value = result.filter(
            log => log.eventType === 'CREDIT_QUOTE_SOLD_OUT'
          );
          askOnSaleLogs.value = result.filter(
            log => log.eventType === 'CREDIT_QUOTE_ON_SALE'
          );
          transactionListLogs.value = result.filter(
            log => log.eventType === 'TRANSACTION_LIST'
          );
        }
      } catch (error) {
        console.error('로그 조회 중 오류 발생:', error);
      }
    };

    watch(selectedDate, () => {
      fetchLogs();
    });

    onMounted(() => {
      fetchLogs();
    });

    // 타입 토글 함수 추가
    const toggleAskType = type => {
      if (selectedAskTypes.value.has(type)) {
        selectedAskTypes.value.delete(type);
      } else {
        selectedAskTypes.value.add(type);
      }
      selectedAskTypes.value = new Set(selectedAskTypes.value);
    };

    const toggleTransactionType = type => {
      if (selectedTransactionTypes.value.has(type)) {
        selectedTransactionTypes.value.delete(type);
      } else {
        selectedTransactionTypes.value.add(type);
      }
      selectedTransactionTypes.value = new Set(selectedTransactionTypes.value);
    };

    return {
      formatTime,
      formatCreditType,
      getCreditTypeClass,
      formatMonths,
      formatDate,
      askListLogs,
      askSoldOutLogs,
      askOnSaleLogs,
      transactionListLogs,
      selectedDate,
      today,
      selectedUser,
      uniqueUsers,
      filteredAskListLogs,
      filteredAskSoldOutLogs,
      filteredAskOnSaleLogs,
      filteredTransactionListLogs,
      totalActivities,
      creditTypeStats,
      statusStats,
      transactionCreditTypeStats,
      selectedAskTypes,
      selectedTransactionTypes,
      toggleAskType,
      toggleTransactionType,
      deduplicateAskList,
      deduplicateTransactionList,
      excludeDefaultMonths,
      excludeDefaultMonthsTransaction,
    };
  },
};
</script>

<style>
/* 기본 폰트 사이즈 관련 스타일 */
:root {
  font-size: 16px;
}

/* 반응형 폰트 사이즈 */
@media screen and (max-width: 768px) {
  :root {
    font-size: 14px;
  }
}

@media screen and (max-width: 480px) {
  :root {
    font-size: 12px;
  }
}

/* 테이블 관련 커스텀 스타일 */
.table-container {
  max-height: 31.25rem; /* 500px -> 31.25rem */
}

/* 배지 스타일 */
.badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
}
</style>
